import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import BasicMenu from '../components/BasicMenu';
import CustomTable from '../components/CustomTable';
import CustomTablePagination from '../components/CustomTablePagination';
import FilterButton from '../components/FilterButton';
import SearchField from '../components/SearchField';
import TablePageHeader from '../components/TablePageHeader';
import { useAlert } from '../hooks/useAlert';
import { downloadInvoice, getInvoices, updateInvoiceStatus } from '../services/admin/AdminServices';
import { baseUrl, fetcher } from '../utils/global';
import { ApiList, Invoice, InvoiceStatus, Jeweler, TableBodyRowType, TableDataType } from '../utils/types';

export default function InvoicesContainer() {
  const { showSnackbar: showSnackBar, showError } = useAlert();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [jewelerAnchorEl, setJewelerAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const jewelerMenuOpen = Boolean(jewelerAnchorEl);
  const [statusFilter, setStatusFilter] = useState('');
  const [jewelerFilter, setJewelerFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tableData, setTableData] = useState<TableDataType>({
    head: [
      { id: 'invoiceNumber', label: 'Fatura No' },
      { id: 'jeweler', label: 'Kuyumcu' },
      { id: 'amount', label: 'Tutar' },
      { id: 'taxAmount', label: 'KDV' },
      { id: 'totalAmount', label: 'Toplam' },
      { id: 'status', label: 'Durum' },
      { id: 'createdAt', label: 'Oluşturma Tarihi' },
      { id: 'dueDate', label: 'Vade Tarihi' },
      { id: 'actions', label: 'İşlemler' }
    ],
    body: []
  });

  const { data: invoices, isLoading, error } = useSWR<ApiList<Invoice>>(
    `${baseUrl}/invoices?skip=${(page - 1) * recordPerPage}&take=${recordPerPage}&search=${search}${statusFilter ? `&status=${statusFilter}` : ''}${jewelerFilter ? `&jewelerId=${jewelerFilter}` : ''}`,
    (url: string) => fetcher(url)
  );

  // Kuyumcu listesini çek
  const { data: jewelers } = useSWR<ApiList<Jeweler>>(
    `${baseUrl}/jewelers?skip=0&take=1000&status=ACTIVE`,
    (url: string) => fetcher(url)
  );

  const convertData = (data: ApiList<Invoice>) => {
    const bodyData: TableBodyRowType[] = data.results.map((invoice) => ({
      rowData: [
        { value: invoice.invoiceNumber || '', type: 'text' },
        { value: invoice.jeweler?.companyName || `${invoice.jeweler?.firstName} ${invoice.jeweler?.lastName}` || '', type: 'text' },
        { value: `₺${invoice.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`, type: 'text' },
        { value: `₺${invoice.taxAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`, type: 'text' },
        { value: `₺${invoice.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`, type: 'text' },
        {
          value: getStatusText(invoice.status),
          id: invoice.status || '',
          type: 'options',
          onSelected: (id: any) => {
            invoice.status !== id && handleUpdateStatus(invoice.id || '', id)
          },
          variant: [
            {
              id: InvoiceStatus.PENDING,
              label: 'Beklemede',
              bgColor: '#FFA5001A',
              textColor: '#FF8C00'
            },
            {
              id: InvoiceStatus.PAID,
              label: 'Ödendi',
              bgColor: '#1CBA761A',
              textColor: '#1CBA76'
            },
            {
              id: InvoiceStatus.OVERDUE,
              label: 'Vadesi Geçmiş',
              bgColor: '#C438251A',
              textColor: '#D43D28'
            },
            {
              id: InvoiceStatus.CANCELLED,
              label: 'İptal',
              bgColor: '#6C757D1A',
              textColor: '#6C757D'
            }
          ]
        },
        { 
          value: new Date(invoice.createdAt).toLocaleDateString('tr-TR'), 
          type: 'text' 
        },
        { 
          value: new Date(invoice.dueDate).toLocaleDateString('tr-TR'), 
          type: 'text' 
        },
        {
          value: '',
          type: 'actions',
          actions: [
            {
              name: 'PDF İndir',
              action: () => handleDownloadInvoice(invoice.id || '')
            },
            {
              name: 'Durum Güncelle',
              action: () => {
                // Status update will be handled by the options column
              }
            }
          ]
        }
      ]
    }));

    setTableData((prev) => ({
      ...prev,
      body: bodyData
    }));
  };

  const getStatusText = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.PENDING:
        return 'Beklemede';
      case InvoiceStatus.PAID:
        return 'Ödendi';
      case InvoiceStatus.OVERDUE:
        return 'Vadesi Geçmiş';
      case InvoiceStatus.CANCELLED:
        return 'İptal';
      default:
        return 'Bilinmiyor';
    }
  };

  useEffect(() => {
    if (invoices) {
      convertData(invoices);
      const totalCount = invoices.total;
      const totalPage = Math.ceil(totalCount / recordPerPage);
      setTotal(totalPage);
    } else {
      setTableData((prev) => ({
        ...prev,
        body: []
      }));
    }
  }, [invoices]);

  useEffect(() => {
    setPage(1);
  }, [recordPerPage, search, statusFilter, jewelerFilter]);

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleJewelerFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setJewelerAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleJewelerClose = () => {
    setJewelerAnchorEl(null);
  };

  const handleFilterSelect = (item: string) => {
    if (item === 'Beklemede') {
      setStatusFilter(InvoiceStatus.PENDING);
    } else if (item === 'Ödendi') {
      setStatusFilter(InvoiceStatus.PAID);
    } else if (item === 'Vadesi Geçmiş') {
      setStatusFilter(InvoiceStatus.OVERDUE);
    } else if (item === 'İptal') {
      setStatusFilter(InvoiceStatus.CANCELLED);
    } else {
      setStatusFilter('');
    }
    setAnchorEl(null);
  }

  const handleJewelerFilterSelect = (jewelerId: string) => {
    setJewelerFilter(jewelerId);
    setJewelerAnchorEl(null);
  }

  const handleUpdateStatus = async (id: string, status: InvoiceStatus) => {
    try {
      await updateInvoiceStatus(id, status);
      mutate(`${baseUrl}/invoices?skip=${(page - 1) * recordPerPage}&take=${recordPerPage}&search=${search}${statusFilter ? `&status=${statusFilter}` : ''}${jewelerFilter ? `&jewelerId=${jewelerFilter}` : ''}`);
      showSnackBar('Fatura durumu başarıyla güncellendi', 'success');
    } catch (e) {
      showError(e, { defaultMessage: 'Fatura durumu güncellenirken bir hata oluştu' });
      console.log(e)
    }
  }

  const handleDownloadInvoice = async (id: string) => {
    try {
      await downloadInvoice(id);
      showSnackBar('Fatura başarıyla indirildi', 'success');
    } catch (e) {
      showError(e, { defaultMessage: 'Fatura indirilirken bir hata oluştu' });
      console.log(e)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '30px',
            fontWeight: 400,
          }}
        >
          Fatura Listesi
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <SearchField
            handleSearch={setSearch}
          />
          <FilterButton
            filterClick={handleJewelerFilterClick}
            label="Kuyumcu"
          />
          <FilterButton
            filterClick={handleFilterClick}
            label="Durum"
          />
        </Box>
      </Box>
      <CustomTable
        data={tableData}
        isLoading={isLoading}
      />
      {total > 1 && (
        <CustomTablePagination
          total={total}
          page={page}
          onPageChange={(page) => setPage(page)}
        />
      )}
      <BasicMenu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        items={['Hepsi', 'Beklemede', 'Ödendi', 'Vadesi Geçmiş', 'İptal']}
        onSelected={handleFilterSelect}
      />
      <BasicMenu
        open={jewelerMenuOpen}
        anchorEl={jewelerAnchorEl}
        onClose={handleJewelerClose}
        items={['Hepsi', ...(jewelers?.results?.map(jeweler => jeweler.companyName || `${jeweler.firstName} ${jeweler.lastName}`) || [])]}
        onSelected={(item) => {
          if (item === 'Hepsi') {
            handleJewelerFilterSelect('');
          } else {
            const selectedJeweler = jewelers?.results?.find(jeweler => 
              (jeweler.companyName || `${jeweler.firstName} ${jeweler.lastName}`) === item
            );
            if (selectedJeweler) {
              handleJewelerFilterSelect(selectedJeweler.id);
            }
          }
        }}
      />
    </Box>
  );
}
